import { useMutation } from '@apollo/client'
import { observer } from 'mobx-react-lite'
import { FOLLOW, UN_FOLLOW } from '~/graphqls/gqls'
import {
  follow,
  followVariables,
  followCancel,
  followCancelVariables,
  FollowStatus,
} from '~/graphqls/types'
import { useStores } from '~/hooks/useStores'
import { MyBtn } from '../ui/MyBtn'
import { ReactEventHandler, useState, useEffect } from 'react'
import { message, Modal } from 'antd'
import { useValidateAuth } from '~/hooks/useValidateAuth'
import classNames from 'classnames'

interface Props {
  targetId?: number
  status?: FollowStatus | null
  /** 是自己时是否隐藏该组件, 默认false */
  hiddenIfSelf?: boolean
  /** 状态变更 */
  onChange?: (status: FollowStatus) => void
  statusDesc?: Record<FollowStatus, { text: string; type: string }>
  dark?: boolean
  className?: string
  followCallback?: (isFollow: boolean) => void
}

const defaultStatusDesc = {
  [FollowStatus.NON]: { text: '关注', type: 'primary' },
  [FollowStatus.HE_ONE_WAY]: { text: '关注', type: 'primary' },
  [FollowStatus.ME_ONE_WAY]: { text: '已关注', type: 'default' },
  [FollowStatus.TWO_WAY]: { text: '互相关注', type: 'default' },
}

const FollowBtn = observer(
  ({
    targetId,
    status: _status,
    hiddenIfSelf = false,
    onChange,
    statusDesc = defaultStatusDesc,
    dark = false,
    className,
    followCallback,
  }: Props) => {
    const { store } = useStores()
    const { validateIsLogin } = useValidateAuth()

    const [loading, setLoading] = useState(false)
    const [status, setStatus] = useState<FollowStatus | undefined | null>(
      _status
    )
    const [followAction] = useMutation<follow, followVariables>(FOLLOW)
    const [unFollowAction] = useMutation<followCancel, followCancelVariables>(
      UN_FOLLOW
    )

    useEffect(() => {
      if (status !== _status && status && onChange) {
        onChange(status)
      }
    }, [status])

    useEffect(() => {
      if (status !== _status) {
        setStatus(_status)
      }
    }, [_status])
    const isSelf = targetId === store.userinfo?.base?.uid

    if (!targetId || !status) {
      return null
    }
    if (isSelf && hiddenIfSelf) {
      return null
    }

    const handleFollow = async () => {
      try {
        setLoading(true)
        const res = await followAction({
          variables: {
            param: {
              followUid: targetId,
            },
          },
        })
        if (res.data?.status) {
          setStatus(prevStatus => {
            switch (prevStatus) {
              case FollowStatus.NON:
                return FollowStatus.ME_ONE_WAY
              case FollowStatus.HE_ONE_WAY:
                return FollowStatus.TWO_WAY
              case FollowStatus.ME_ONE_WAY:
                // never
                return FollowStatus.ME_ONE_WAY
              case FollowStatus.TWO_WAY:
                // never
                return FollowStatus.TWO_WAY
            }
          })
          followCallback?.(true)
        }
      } catch (err) {
        console.error(err)
      } finally {
        setLoading(false)
      }
    }

    const handleUnFollow = async () => {
      let resolveP: any
      let rejectP: any
      const confirmPromise = new Promise((resolve, reject) => {
        resolveP = resolve
        rejectP = reject
      })
      Modal.confirm({
        title: '提示',
        maskStyle: {
          backgroundColor: 'rgba(0,0,0,0.8)',
        },
        width: 270,
        className: `hookModal dark`,
        content: (
          <div className="text-neutral-400 text-sm text-center">
            确认不再关注？
          </div>
        ),
        okText: '提交',
        onOk: close => {
          resolveP()
          close()
        },
        onCancel: close => {
          rejectP()
          close()
        },
      })
      try {
        await confirmPromise
      } catch (err) {
        return
      }

      try {
        setLoading(true)
        const res = await unFollowAction({
          variables: {
            param: {
              followUid: targetId,
            },
          },
        })
        if (res.data?.status) {
          setStatus(prevStatus => {
            switch (prevStatus) {
              case FollowStatus.NON:
                // never
                return FollowStatus.NON
              case FollowStatus.HE_ONE_WAY:
                // never
                return FollowStatus.HE_ONE_WAY
              case FollowStatus.ME_ONE_WAY:
                return FollowStatus.NON
              case FollowStatus.TWO_WAY:
                return FollowStatus.HE_ONE_WAY
            }
          })
          followCallback?.(false)
        }
      } catch (err) {
        console.error(err)
      } finally {
        setLoading(false)
      }
    }

    const handleClick: ReactEventHandler = e => {
      if (!validateIsLogin()) return

      if (isSelf) {
        message.destroy()
        message.error('不能关注自己')
        return
      }

      if (
        status === FollowStatus.ME_ONE_WAY ||
        status == FollowStatus.TWO_WAY
      ) {
        handleUnFollow()
      } else {
        handleFollow()
      }
    }

    const getStatusVal = () => {
      let text = '关注'
      let type: 'default' | 'primary' = 'default'
      if (statusDesc[status]) {
        text = statusDesc[status].text
        type = statusDesc[status].type as 'primary' | 'default'
      }
      return {
        text,
        type,
      }
    }

    return (
      // 内部click事件到此为止，这样该元素可以作为Link的子元素
      <span onClick={e => e.preventDefault()}>
        <MyBtn
          $dark={dark}
          size="small"
          type={getStatusVal().type}
          className={classNames('w-20 !h-7 !p-0', className)}
          loading={loading}
          onClick={handleClick}
        >
          {getStatusVal().text}
        </MyBtn>
      </span>
    )
  }
)

export default FollowBtn
